import type { ConfirmFn } from "@/components/confirm-provider";

// Shared confirmation dialogs. Each takes the `confirm` fn from `useConfirm()`
// so the wording/styling of a given kind of prompt lives in exactly one place.

/** Minimal shape needed to describe an order in a cancel confirmation. */
export interface CancelableOrder {
  orderId: number;
  underlying: string;
  optionSymbol: string;
  /** Long leg for spreads; absent for single-leg orders. */
  longOptionSymbol?: string;
}

/**
 * Where a cancel confirm is raised. The "Don't show this again" choice is
 * remembered per user/account/strategy/pane, so suppression on one strategy's
 * Open Orders pane never silences another's.
 */
export interface CancelConfirmScope {
  accountHash: string;
  strategy: string; // "vs" | "vs0" | "vse" | "cc" | "trades"
  pane: string;     // e.g. "open-orders"
}

const suppressKey = (s: CancelConfirmScope) =>
  `cancelConfirmSuppress:${s.accountHash}:${s.strategy}:${s.pane}`;

function isCancelSuppressed(s: CancelConfirmScope): boolean {
  try {
    return localStorage.getItem(suppressKey(s)) === "true";
  } catch {
    return false;
  }
}

function setCancelSuppressed(s: CancelConfirmScope): void {
  try {
    localStorage.setItem(suppressKey(s), "true");
  } catch {
    /* private mode / storage disabled — fall back to always confirming */
  }
}

/**
 * Confirm cancelling an order. Lists every leg (both legs of a spread, each on
 * its own line). Used by every cancel-order path (VS, 0DTE, covered calls, Trades).
 *
 * Pass `scope` to offer a "Don't show this again" checkbox: once checked and
 * confirmed, future cancels in that same (account, strategy, pane) skip the
 * dialog and resolve `true` immediately. Omit `scope` to always confirm.
 */
export async function confirmCancelOrder(
  confirm: ConfirmFn,
  o: CancelableOrder,
  scope?: CancelConfirmScope,
): Promise<boolean> {
  if (scope && isCancelSuppressed(scope)) return true;
  const legs = [o.optionSymbol, o.longOptionSymbol].filter(Boolean).join("\n");
  let dontShowAgain = false;
  const ok = await confirm({
    title: "Cancel order",
    message: `Cancel order #${o.orderId} — ${o.underlying}?\n${legs}`,
    confirmText: "Cancel order",
    cancelText: "Keep",
    danger: true,
    checkbox: scope ? "Don't show this again" : undefined,
    onCheckbox: (c) => { dontShowAgain = c; },
  });
  if (ok && dontShowAgain && scope) setCancelSuppressed(scope);
  return ok;
}

/** Confirm copying a strategy's settings from a paper account (overwrites). */
export function confirmCopySettings(
  confirm: ConfirmFn,
  strategy: string,
  sourceLabel: string,
): Promise<boolean> {
  return confirm({
    title: "Copy settings",
    message: `Copy all ${strategy} settings from "${sourceLabel}" into this account? This overwrites the current settings.`,
    confirmText: "Copy",
  });
}

/** Confirm copying another user's saved defaults into this account (overwrites). */
export function confirmCopyDefaults(
  confirm: ConfirmFn,
  strategy: string,
  username: string,
  accountLabel: string,
): Promise<boolean> {
  return confirm({
    title: "Copy defaults from another user",
    message: `Copy ${strategy} defaults from ${username} — ${accountLabel} into this account? This overwrites the current settings.`,
    confirmText: "Copy defaults",
  });
}

/** Confirm placing an order, showing the order summary lines. */
export function confirmPlaceOrder(confirm: ConfirmFn, lines: string[]): Promise<boolean> {
  return confirm({
    title: "Confirm order",
    message: lines.join("\n"),
    confirmText: "Place order",
  });
}

/** Confirm buying back an open position at the current mid. */
export function confirmExitAtMid(
  confirm: ConfirmFn,
  lines: string[],
): Promise<boolean> {
  return confirm({
    title: "Exit position",
    message: lines.join("\n"),
    confirmText: "Exit",
    danger: true,
  });
}

/**
 * Where a delete confirm is raised. The "Don't show this again" choice is
 * remembered per user/account/strategy/pane, so suppression on one pane never
 * silences another (e.g. All Trades vs Clear-all).
 */
export interface DeleteConfirmScope {
  accountHash: string;
  strategy: string; // "vs" | "vs0" | "vse" | "cc" | "trades"
  pane: string;     // e.g. "all-trades"
}

const deleteSuppressKey = (s: DeleteConfirmScope) =>
  `deleteConfirmSuppress:${s.accountHash}:${s.strategy}:${s.pane}`;

function isDeleteSuppressed(s: DeleteConfirmScope): boolean {
  try {
    return localStorage.getItem(deleteSuppressKey(s)) === "true";
  } catch {
    return false;
  }
}

function setDeleteSuppressed(s: DeleteConfirmScope): void {
  try {
    localStorage.setItem(deleteSuppressKey(s), "true");
  } catch {
    /* private mode / storage disabled — fall back to always confirming */
  }
}

/**
 * Generic destructive delete confirm (red button). `all: true` switches the
 * title/button to "Delete all"; `title` overrides the heading (e.g. "Delete
 * position") while keeping the standard button label.
 *
 * Pass `scope` to offer a "Don't show this again" checkbox: once checked and
 * confirmed, future deletes in that same (account, strategy, pane) skip the
 * dialog and resolve `true` immediately. Omit `scope` to always confirm.
 */
export async function confirmDelete(
  confirm: ConfirmFn,
  message: string,
  opts?: { all?: boolean; title?: string; scope?: DeleteConfirmScope },
): Promise<boolean> {
  if (opts?.scope && isDeleteSuppressed(opts.scope)) return true;
  const confirmText = opts?.all ? "Delete all" : "Delete";
  let dontShowAgain = false;
  const ok = await confirm({
    title: opts?.title ?? confirmText,
    message,
    confirmText,
    danger: true,
    checkbox: opts?.scope ? "Don't show this again" : undefined,
    onCheckbox: (c) => { dontShowAgain = c; },
  });
  if (ok && dontShowAgain && opts?.scope) setDeleteSuppressed(opts.scope);
  return ok;
}
